import Head from 'next/head'
import React, { ReactNode, useCallback, useState } from 'react'
import { Auth0Provider } from '@auth0/auth0-react'
import { Link } from '@scentregroup/shared/link'
import { CookiesProvider } from 'react-cookie'
import {
  CountryContext,
  GetHrefContext,
  LinkContext,
} from '@scentregroup/shared/context'
import { HubProvider } from '@hub/design-system-base'
import {
  CONFIG_KEYS,
  NATIONAL_CONFIG,
  getClientConfigBool,
} from '@scentregroup/shared/config'
import { isCookieEnabledBrowser } from '../src/shared/lib/env'
import { SiteConfigurationContext } from '../src/client/lib/site-configuration'
import { GiftcardsConfigurationContext } from '../src/client/lib/giftcards-configuration'
import HttpStatusCode from '../src/server/lib/http-codes'
import { AppStateProvider } from '../src/client/components/state-provider'
import StripeWrapper from '../src/client/components/stripe-wrapper'
import ErrorBoundary from '../src/client/components/error-boundary'
import { Country } from '../src/shared/types/country'
import ErrorPageNext from 'next/error'
import { useRouter } from 'next/router'
import {
  SessionIdProvider,
  CookieSessionIdStorage,
} from '@scentregroup/shared/session-id'
import FeatureFlagsProvider, {
  useFeatureFlagOverridesFromQuery,
} from '@scentregroup/shared/components/feature-flags-provider'
import {
  AnalyticsProvider,
  ConsoleAnalyticsHandler,
} from '@scentregroup/shared/analytics'
import {
  AnalyticsNextJSPageTracker,
  AnalyticsUserIdMonitor,
} from '@scentregroup/shared/analytics-helpers'
import { DataLayerAnalyticsHandler } from '@scentregroup/shared/analytics-datalayer'
import {
  useSegmentConfiguration,
  SegmentProvider,
  SegmentAnalyticsHandler,
} from '@scentregroup/shared/analytics-segment'
import RouteChangeIndicator from '@scentregroup/shared/hub-components/route-change-indicator'
import { Spinner } from '@scentregroup/ui'
import { ApolloProvider } from '../components/apollo-provider'
import UserStateProvider from '@scentregroup/shared/components/user-state-provider'
import { isBrowser } from '@scentregroup/shared/env'
import { RawCookieName } from '@scentregroup/shared/constants'
import CashbackTransactionDetector from '@scentregroup/shared/components/cashback-transaction-detector'
import CouponCodeDetector from '@scentregroup/shared/components/coupon-code-detector'
import '@scentregroup/ui/fonts/index.css'
import '@scentregroup/ui/tools/reset.css'
import { UniversalHeaderSearchContextProvider } from '@scentregroup/shared/components/search/use-universal-header-search'
import '../styles/globals.scss'
import '../src/client/components/date-picker/date-picker.scss'
import { AnalyticsImpressionTrackerProvider } from '@scentregroup/shared/analytics-observer/analytics-impression-tracker-context'
import GoogleTagManager, {
  GoogleTagManagerData,
} from '@scentregroup/shared/hub-components/google-tag-manager'
import useIsMounted from '@scentregroup/shared/hooks/use-is-mounted'
import { getSiteConfiguration } from '../lib/site-configuration'

const CookiesWrapper: React.FunctionComponent<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  return isBrowser() && !isCookieEnabledBrowser() ? (
    <ErrorPageNext
      statusCode={HttpStatusCode.BAD_REQUEST}
      title="Cookies are disabled  :(  Please enable cookies and try again"
    ></ErrorPageNext>
  ) : (
    <CookiesProvider>{children}</CookiesProvider>
  )
}

const cookieSessionIdStorage = new CookieSessionIdStorage({
  cookieName: RawCookieName.SESSION_ID,
  expiryInDays: 8,
})

interface IAppProps<P> {
  Component: React.FunctionComponent<React.PropsWithChildren<unknown>>
  pageProps: P
}

function App<P extends { children?: ReactNode }>({
  Component,
  pageProps,
}: IAppProps<P>): React.ReactElement<IAppProps<P>> {
  const router = useRouter()
  const isBrowser = useIsMounted()

  const [gcFromSearch, setGcFromSearch] = useState(false)
  const country =
    router.locale === 'en-NZ' ? Country.NEW_ZEALAND : Country.AUSTRALIA

  const giftCardsConfiguration = {
    gcFromSearch,
    setGcFromSearch,
  }

  const META_INFORMATION =
    router.locale === 'en-NZ' ? NATIONAL_CONFIG.nz : NATIONAL_CONFIG.au

  const getHref = useCallback(
    () =>
      (isBrowser && window.location.href) ||
      `${META_INFORMATION.url}${router.basePath}${router.asPath}`,
    [isBrowser, META_INFORMATION, router.asPath, router.basePath]
  )

  const featureFlagOverrides = useFeatureFlagOverridesFromQuery(router.query)

  const segmentConfiguration = useSegmentConfiguration(country)

  return (
    <>
      <Head>
        <link rel="preconnect" href="https://res.cloudinary.com" />
        <link rel="preconnect" href="https://images.scentregroup.io" />
        {router.query.q && <meta name="robots" content="noindex" />}
      </Head>
      <GoogleTagManager
        containerId={process.env.NEXT_PUBLIC_GTM_ID}
        preview={process.env.NEXT_PUBLIC_GTM_PREVIEW_ENV}
        auth={process.env.NEXT_PUBLIC_GTM_PREVIEW_AUTH}
      />
      <GoogleTagManagerData />
      <LinkContext.Provider value={Link}>
        <HubProvider Link={Link}>
          <SessionIdProvider storage={cookieSessionIdStorage}>
            <CountryContext.Provider value={country}>
              <Auth0Provider
                domain={process.env.NEXT_PUBLIC_AUTH0_DOMAIN ?? ''}
                clientId={process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID ?? ''}
                authorizationParams={{
                  redirect_uri: isBrowser
                    ? `${window.location.origin}/account/callback`
                    : undefined,
                }}
              >
                <ApolloProvider>
                  <FeatureFlagsProvider
                    doGetFromEnv
                    overrides={featureFlagOverrides}
                  >
                    <>
                      <SiteConfigurationContext.Provider
                        value={getSiteConfiguration(country)}
                      >
                        <GiftcardsConfigurationContext.Provider
                          value={giftCardsConfiguration}
                        >
                          <CookiesWrapper>
                            <AnalyticsProvider>
                              <AnalyticsImpressionTrackerProvider>
                                {getClientConfigBool(
                                  CONFIG_KEYS.NEXT_PUBLIC_FEAT_FLAG_ANALYTICS_CONSOLE
                                ) && <ConsoleAnalyticsHandler />}
                                <DataLayerAnalyticsHandler />
                                {segmentConfiguration && (
                                  <SegmentProvider {...segmentConfiguration}>
                                    <SegmentAnalyticsHandler />
                                  </SegmentProvider>
                                )}
                                <AnalyticsUserIdMonitor />
                                <AnalyticsNextJSPageTracker />
                                <ErrorBoundary router={router}>
                                  <StripeWrapper>
                                    <GetHrefContext.Provider value={getHref}>
                                      <UserStateProvider>
                                        <AppStateProvider>
                                          <RouteChangeIndicator
                                            fallback={() => (
                                              <div
                                                style={{
                                                  width: '100%',
                                                  height: '100%',
                                                }}
                                              >
                                                <Spinner
                                                  type="large"
                                                  isSeasonal={false}
                                                />
                                              </div>
                                            )}
                                          >
                                            <UniversalHeaderSearchContextProvider>
                                              <Component {...pageProps} />
                                            </UniversalHeaderSearchContextProvider>
                                          </RouteChangeIndicator>
                                        </AppStateProvider>
                                      </UserStateProvider>
                                    </GetHrefContext.Provider>
                                  </StripeWrapper>
                                </ErrorBoundary>
                              </AnalyticsImpressionTrackerProvider>
                            </AnalyticsProvider>
                          </CookiesWrapper>
                        </GiftcardsConfigurationContext.Provider>
                      </SiteConfigurationContext.Provider>
                      <CashbackTransactionDetector />
                      <CouponCodeDetector />
                    </>
                  </FeatureFlagsProvider>
                </ApolloProvider>
              </Auth0Provider>
            </CountryContext.Provider>
          </SessionIdProvider>
        </HubProvider>
      </LinkContext.Provider>
    </>
  )
}

export default App
